import { formatSupplierTypes } from '../../utils/supplierTypes';
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Supplier, SupplierDeal, suppliersAPI, supplierDealsAPI, purchaseOrdersAPI, PurchaseOrder } from '../../services/api';
import {
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
  DocumentPlusIcon,
  BuildingOfficeIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  CalendarIcon,
  TruckIcon,
  ChartBarIcon,
  UserGroupIcon,
  CubeIcon,
  ClockIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';
import { Card, CardBody, Button, Badge, Spinner, EmptyState } from '../ui';

const CATALOGUE_PRODUCT_TYPE_LABELS: Record<string, string> = {
  PRODUCT: 'Product',
  RAW_MATERIAL: 'Raw Material',
  SPICES: 'Spices',
  HERBS: 'Herbs',
  OILS_AND_OIL_SEEDS: 'Oils & Oils Seeds',
  COSMETIC_AND_NATURAL_BEAUTY: 'Cosmetic & Natural Beauty',
  DRIED_FOOD: 'Dried Food',
  SEEDS: 'Seeds',
  EXTRACTS: 'Extracts',
  HERBAL_INFUSIONS: 'Herbal Infusions',
};

const SupplierDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const [deals, setDeals] = useState<SupplierDeal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);

  useEffect(() => {
    if (id) {
      fetchSupplierData();
    }
  }, [id]);

  const fetchSupplierData = async () => {
    try {
      setLoading(true);
      const [supplierData, dealsData] = await Promise.all([
        suppliersAPI.getById(parseInt(id!)),
        suppliersAPI.getDeals(parseInt(id!)),
      ]);
      setSupplier(supplierData);
      setDeals(dealsData);

      // Fetch purchase orders for this supplier
      try {
        const poRes = await purchaseOrdersAPI.getAll({ supplierId: parseInt(id!), limit: 100 });
        setPurchaseOrders(poRes.data || []);
      } catch { /* PO endpoint may not be available */ }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch supplier details');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await suppliersAPI.delete(parseInt(id!));
      navigate('/admin/suppliers');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete supplier');
    }
  };

  const getSupplierTypeIcon = (type: string) => {
    switch (type) {
      case 'MANUFACTURER':
      case 'CONTRACT_MANUFACTURER':
        return <BuildingOfficeIcon className="h-6 w-6" />;
      case 'DISTRIBUTOR':
      case 'WHOLESALER':
        return <TruckIcon className="h-6 w-6" />;
      default:
        return <BuildingOfficeIcon className="h-6 w-6" />;
    }
  };

  const getImportanceBadge = (importance: string) => {
    switch (importance) {
      case 'VERY_HIGH':
        return 'bg-error-100 text-error-800';
      case 'HIGH':
        return 'bg-warning-100 text-warning-800';
      case 'MEDIUM':
        return 'bg-warning-100 text-warning-800';
      case 'LOW':
        return 'bg-primary-100 text-primary-800';
      case 'VERY_LOW':
        return 'bg-neutral-100 text-neutral-800';
      default:
        return 'bg-neutral-100 text-neutral-800';
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const totalSpent = deals.reduce((sum, deal) => sum + parseFloat(deal.amount.toString()), 0);
  const avgDealSize = deals.length > 0 ? totalSpent / deals.length : 0;

  const getDisplayType = formatSupplierTypes;

  if (loading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <Spinner size="lg" />
        </div>
      </div>
    );
  }

  if (error || !supplier) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-error-50 border border-error-200 text-error-700 px-4 py-3 rounded">
          {error || 'Supplier not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <Link
          to="/admin/suppliers"
          className="inline-flex items-center text-sm text-neutral-500 hover:text-neutral-700 mb-4"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Suppliers
        </Link>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className={`p-3 rounded-lg ${getImportanceBadge(supplier.importance)}`}>
              {getSupplierTypeIcon(supplier.type)}
            </div>
            <div>
              <h1 className="text-3xl font-bold text-neutral-900">{supplier.name}</h1>
              <div className="flex items-center space-x-4 mt-2">
                <Badge variant="primary">
                  {getDisplayType(supplier)}
                </Badge>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getImportanceBadge(supplier.importance)}`}>
                  {supplier.importance.replace(/_/g, ' ')} Priority
                </span>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${supplier.status === 'ACTIVE_SUPPLIER'
                  ? 'bg-success-100 text-success-800'
                  : 'bg-secondary-100 text-secondary-800'
                  }`}>
                  {supplier.status === 'ACTIVE_SUPPLIER' ? 'Supplier' : 'Potential Supplier'}
                </span>
              </div>
            </div>
          </div>
          <div className="flex space-x-3">
            <Link to={`/admin/suppliers/${supplier.id}/edit`}>
              <Button variant="outline" leftIcon={<PencilIcon className="h-4 w-4" />}>
                Edit
              </Button>
            </Link>
            {supplier.status === 'ACTIVE_SUPPLIER' && (
              <Link to={`/admin/purchase-orders/new?supplierId=${supplier.id}`}>
                <Button variant="primary" leftIcon={<DocumentPlusIcon className="h-4 w-4" />}>
                  Create PO
                </Button>
              </Link>
            )}
            {deleteConfirm ? (
              <div className="flex space-x-2">
                <Button variant="danger" onClick={handleDelete}>
                  Confirm Delete
                </Button>
                <Button variant="outline" onClick={() => setDeleteConfirm(false)}>
                  Cancel
                </Button>
              </div>
            ) : (
              <Button
                variant="danger"
                onClick={() => setDeleteConfirm(true)}
                leftIcon={<TrashIcon className="h-4 w-4" />}
              >
                Delete
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-primary-100 text-sm font-medium">Total Spent</p>
              <p className="text-3xl font-bold mt-2">{formatCurrency(totalSpent)}</p>
            </div>
            <CurrencyDollarIcon className="h-12 w-12 text-primary-200" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-success-500 to-success-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-success-100 text-sm font-medium">Total Deals</p>
              <p className="text-3xl font-bold mt-2">{deals.length}</p>
            </div>
            <ChartBarIcon className="h-12 w-12 text-success-200" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-secondary-500 to-secondary-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-secondary-100 text-sm font-medium">Avg Deal Size</p>
              <p className="text-3xl font-bold mt-2">{formatCurrency(avgDealSize)}</p>
            </div>
            <DocumentTextIcon className="h-12 w-12 text-secondary-200" />
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <Card variant="elevated" className="mb-8">
        <div className="px-6 py-4 border-b border-neutral-200">
          <h2 className="text-lg font-semibold text-neutral-900">Contact Information</h2>
        </div>
        <CardBody className="px-6 py-6">
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {supplier.emails && supplier.emails.length > 0 && (
              <div className="flex items-start">
                <EnvelopeIcon className="h-6 w-6 text-neutral-400 mr-3 mt-0.5" />
                <div>
                  <dt className="text-sm font-medium text-neutral-500">Emails</dt>
                  <dd className="mt-1 space-y-1">
                    {supplier.emails.map((email, index) => (
                      <div key={index} className="text-sm text-neutral-900">
                        <a href={`mailto:${email}`} className="text-primary-600 hover:text-primary-900">
                          {email}
                        </a>
                      </div>
                    ))}
                  </dd>
                </div>
              </div>
            )}
            {supplier.websites && supplier.websites.length > 0 && (
              <div className="flex items-start">
                <GlobeAltIcon className="h-6 w-6 text-neutral-400 mr-3 mt-0.5" />
                <div>
                  <dt className="text-sm font-medium text-neutral-500">Websites</dt>
                  <dd className="mt-1 space-y-1">
                    {supplier.websites.map((website, index) => (
                      <div key={index} className="text-sm text-neutral-900">
                        <a href={website.startsWith('http') ? website : `https://${website}`} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-900">
                          {website}
                        </a>
                      </div>
                    ))}
                  </dd>
                </div>
              </div>
            )}
            {supplier.phoneNumbers && supplier.phoneNumbers.length > 0 && (
              <div className="flex items-start">
                <PhoneIcon className="h-6 w-6 text-neutral-400 mr-3 mt-0.5" />
                <div>
                  <dt className="text-sm font-medium text-neutral-500">Phone Numbers</dt>
                  <dd className="mt-1 space-y-1">
                    {supplier.phoneNumbers.map((phone, index) => (
                      <div key={index} className="text-sm text-neutral-900">
                        <a href={`tel:${phone}`} className="text-primary-600 hover:text-primary-900">
                          {phone}
                        </a>
                      </div>
                    ))}
                  </dd>
                </div>
              </div>
            )}
            {supplier.address && (
              <div className="flex items-start">
                <MapPinIcon className="h-6 w-6 text-neutral-400 mr-3 mt-0.5" />
                <div>
                  <dt className="text-sm font-medium text-neutral-500">Address</dt>
                  <dd className="mt-1 text-sm text-neutral-900 whitespace-pre-line">{supplier.address}</dd>
                </div>
              </div>
            )}
          </dl>
        </CardBody>
      </Card>

      {/* People / Contacts */}
      <Card variant="elevated" className="mb-8">
        <div className="px-6 py-4 border-b border-neutral-200">
          <div className="flex items-center space-x-2">
            <UserGroupIcon className="h-5 w-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-neutral-900">People / Contacts</h2>
          </div>
        </div>
        {supplier.contacts && supplier.contacts.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-neutral-200">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Phone</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-neutral-200">
                {supplier.contacts.map((contact) => (
                  <tr key={contact.id} className="hover:bg-neutral-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-neutral-900">{contact.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-600">
                      <Badge variant="secondary">{contact.role}</Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-600">
                      {contact.email ? (
                        <a href={`mailto:${contact.email}`} className="text-primary-600 hover:text-primary-900">{contact.email}</a>
                      ) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-600">
                      {contact.phone ? (
                        <a href={`tel:${contact.phone}`} className="text-primary-600 hover:text-primary-900">{contact.phone}</a>
                      ) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            icon={<UserGroupIcon className="h-12 w-12" />}
            title="No contacts"
            description="No people/contacts have been added for this supplier."
          />
        )}
      </Card>

      {/* Catalogue Products */}
      <Card variant="elevated" className="mb-8">
        <div className="px-6 py-4 border-b border-neutral-200">
          <div className="flex items-center space-x-2">
            <CubeIcon className="h-5 w-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-neutral-900">Catalogue Products</h2>
          </div>
        </div>
        {supplier.catalogueProducts && supplier.catalogueProducts.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-neutral-200">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">SKU</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Cost</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Lead Time</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-neutral-200">
                {supplier.catalogueProducts.map((product) => (
                  <tr key={product.id} className="hover:bg-neutral-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                      <Badge variant={product.type === 'PRODUCT' ? 'primary' : 'secondary'}>
                        {product.productType?.name || (product.type ? (CATALOGUE_PRODUCT_TYPE_LABELS[product.type] || product.type.replace(/_/g, ' ')) : '-')}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-600 font-mono">
                      {product.sku || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-neutral-900">
                      {product.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-600">
                      {product.category?.name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-neutral-900">
                      {formatCurrency(parseFloat(product.cost?.toString() || '0'))}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-600">
                      {product.leadTime ? (
                        <span className="flex items-center">
                          <ClockIcon className="h-4 w-4 mr-1 text-neutral-400" />
                          {product.leadTime}
                        </span>
                      ) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            icon={<CubeIcon className="h-12 w-12" />}
            title="No catalogue products"
            description="No catalogue products have been added for this supplier."
          />
        )}
      </Card>

      {/* Notes */}
      {supplier.notes && (
        <Card variant="elevated" className="mb-8">
          <div className="px-6 py-4 border-b border-neutral-200">
            <h2 className="text-lg font-semibold text-neutral-900">Notes</h2>
          </div>
          <CardBody className="px-6 py-6">
            <p className="text-sm text-neutral-700 whitespace-pre-line">{supplier.notes}</p>
          </CardBody>
        </Card>
      )}

      {/* Purchase Orders */}
      {supplier.status === 'ACTIVE_SUPPLIER' && (
        <Card variant="elevated" className="mb-8">
          <div className="px-6 py-4 border-b border-neutral-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <DocumentPlusIcon className="h-5 w-5 text-primary-600" />
                <h2 className="text-lg font-semibold text-neutral-900">Purchase Orders</h2>
                {purchaseOrders.length > 0 && (
                  <Badge variant="primary" size="sm">{purchaseOrders.length}</Badge>
                )}
              </div>
              <Link to={`/admin/purchase-orders/new?supplierId=${supplier.id}`}>
                <Button variant="outline" size="sm" leftIcon={<DocumentPlusIcon className="h-4 w-4" />}>
                  New PO
                </Button>
              </Link>
            </div>
          </div>
          {purchaseOrders.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-neutral-200">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">PO Number</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Company</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">Amount</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-neutral-200">
                  {purchaseOrders.map((po) => {
                    const statusColors: Record<string, string> = {
                      CREATED: 'bg-neutral-100 text-neutral-800',
                      SENT: 'bg-warning-100 text-warning-800',
                      CONFIRMED: 'bg-primary-100 text-primary-800',
                      RECEIVED: 'bg-success-100 text-success-800',
                    };
                    return (
                      <tr
                        key={po.id}
                        onClick={() => navigate(`/admin/purchase-orders/${po.id}`)}
                        className="hover:bg-primary-50 cursor-pointer transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-primary-600">
                          {po.poNumber}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[po.status] || 'bg-neutral-100 text-neutral-800'}`}>
                            {po.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-600">
                          {po.company?.name || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-600">
                          <div className="flex items-center">
                            <CalendarIcon className="h-4 w-4 text-neutral-400 mr-2" />
                            {formatDate(po.createdAt)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-neutral-900 text-right">
                          {po.currency} {Number(po.totalAmount).toFixed(2)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState
              icon={<DocumentPlusIcon className="h-12 w-12" />}
              title="No purchase orders"
              description="No purchase orders have been created for this supplier yet."
            />
          )}
        </Card>
      )}

      {/* Deals History */}
      <Card variant="elevated">
        <div className="px-6 py-4 border-b border-neutral-200">
          <h2 className="text-lg font-semibold text-neutral-900">Deals History</h2>
        </div>
        {deals.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-neutral-200">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Currency
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Details
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-neutral-200">
                {deals.map((deal) => (
                  <tr key={deal.id} className="hover:bg-neutral-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                      <div className="flex items-center">
                        <CalendarIcon className="h-4 w-4 text-neutral-400 mr-2" />
                        {formatDate(deal.createdAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-neutral-900">
                      {deal.description || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-neutral-900">
                      {formatCurrency(parseFloat(deal.amount.toString()))}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500">
                      {deal.currency}
                    </td>
                    <td className="px-6 py-4 text-sm text-neutral-500">
                      {deal.details || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            icon={<DocumentTextIcon className="h-12 w-12" />}
            title="No deals yet"
            description="No purchase history available for this supplier."
          />
        )}
      </Card>
    </div>
  );
};

export default SupplierDetail;
